import { Injectable } from '@angular/core';
import { SearchResult } from 'src/app/shared';
import { Http, HttpOptions } from 'src/app/shared/services/http.service';
import {
  InventoryAttachmentUploadResponse,
  InventorySubmissionDraftRequest,
  InventorySubmissionSaveResponse,
  SubmitInventoryRequest,
  SubmitInventoryResponse,
} from '../../models/inventory/inventory-request';

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  constructor(private http: Http) {}

  async getDraft(
    source: string,
    claimId: string,
    lob: string
  ): Promise<InventorySubmissionDraftRequest | null> {
    const path = `/api/inventorysubmission/${source}/${claimId}/${lob}/GetDraft`;
    return await this.http.get<InventorySubmissionDraftRequest | null>(path, {
      cache: false,
    });
  }

  async saveDraft(
    source: string,
    claimId: string,
    lob: string,
    draft: InventorySubmissionDraftRequest,
    claim: SearchResult,
    markSaveForLater = false
  ): Promise<InventorySubmissionSaveResponse> {
    const query = markSaveForLater ? '?markSaveForLater=true' : '';
    const path = `/api/inventorysubmission/${source}/${claimId}/${lob}/SaveDraft${query}`;
    return await this.http.post<InventorySubmissionSaveResponse>(
      path,
      draft,
      null,
      'InventorySubmission',
      'Save for later',
      claim
    );
  }

  async clearDraft(
    source: string,
    claimId: string,
    lob: string,
    claim: SearchResult
  ): Promise<InventorySubmissionSaveResponse> {
    const path = `/api/inventorysubmission/${source}/${claimId}/${lob}/ClearDraft`;
    return await this.http.post<InventorySubmissionSaveResponse>(
      path,
      {},
      null,
      'InventorySubmission',
      'Cancel inventory submission',
      claim
    );
  }

  async uploadAttachment(
    source: string,
    claimId: string,
    lob: string,
    file: File,
    itemId: number,
    claim: SearchResult
  ): Promise<InventoryAttachmentUploadResponse> {
    if (!file?.size) {
      return {
        success: false,
        fileName: file?.name,
        statusDescription: 'File content is missing.',
      };
    }

    const formData = new FormData();
    formData.append('file0', file, file.name);
    const path = `/api/inventorysubmission/${source}/${claimId}/${lob}/UploadAttachment?itemId=${itemId}`;
    const options = new HttpOptions(false);
    options.timeout = 60 * 60 * 1000; // 1 hour — large uploads may take a while
    return await this.http.post<InventoryAttachmentUploadResponse>(
      path,
      formData,
      options,
      'InventorySubmission',
      'Upload inventory attachment',
      claim
    );
  }

  async deleteAttachment(
    source: string,
    claimId: string,
    lob: string,
    attachmentId: number,
    claim: SearchResult
  ): Promise<InventorySubmissionSaveResponse> {
    const path = `/api/inventorysubmission/${source}/${claimId}/${lob}/DeleteAttachment/${attachmentId}`;
    return await this.http.post<InventorySubmissionSaveResponse>(
      path,
      {},
      null,
      'InventorySubmission',
      'Delete inventory attachment',
      claim
    );
  }

  submitInventory = async (
    submitInventoryRequest: SubmitInventoryRequest,
    claim: SearchResult
  ) => {
    const path = `/api/inventorysubmission/SubmitInventory`;
    return await this.http.post<SubmitInventoryResponse>(
      path,
      submitInventoryRequest,
      null,
      'InventorySubmission',
      'Submit completed inventory',
      claim
    );
  };
}
